import { GridStackEngine, GridStackMoveOpts, GridStackNode } from 'gridstack';

type InternalGridNode = GridStackNode & { _moving?: boolean; _dirty?: boolean };

/**
 * Custom engine used by dashboard workspace to keep drag interactions stable:
 * prefer pair swaps, avoid push chains, and damp rapid swap oscillation.
 */
export class StableCompactGridStackEngine extends GridStackEngine {
  private static readonly SWAP_COOLDOWN_MS = 300;
  private readonly _swapCooldownByNode = new Map<number, { otherId: number; expiresAt: number }>();
  private readonly _nodeSwapThrottle = new Map<number, number>();

  override moveNode(node: GridStackNode, o: GridStackMoveOpts): boolean {
    if (!node || !o) {
      return false;
    }

    const requestedW = typeof o.w === 'number' ? o.w : node.w;
    const requestedH = typeof o.h === 'number' ? o.h : node.h;
    const isResize = requestedW !== node.w || requestedH !== node.h;
    const movingNode = node as InternalGridNode;
    const isActiveDrag = !!movingNode._moving && !o.nested && !isResize;
    const nodeId = this.getNodeId(node);

    if (!isActiveDrag) {
      this._swapCooldownByNode.delete(nodeId);
      this._nodeSwapThrottle.delete(nodeId);
    }

    // During resize, prefer a same-row horizontal displacement for a collided
    // neighbour. If that is not possible, fall back to GridStack's default
    // resize behavior so vertical growth can still push stacked widgets down.
    if (isResize) {
      // Workspace only exposes e/se/s resize handles, so resizing should keep the
      // widget anchored at its current left edge.
      const requestedX = node.x ?? 0;
      const requestedY = typeof o.y === 'number' ? o.y : (node.y ?? 0);
      const nextPos: GridStackNode = { x: requestedX, y: requestedY, w: requestedW, h: requestedH };

      // For pure vertical resize (or shrink), keep default GridStack behavior so
      // stacked widgets can be pushed down as the upper one grows.
      const widthGrowing = requestedW > (node.w ?? 0);
      if (!widthGrowing) {
        return super.moveNode(node, { ...o, x: requestedX, y: requestedY });
      }

      const collide = this.collide(node, nextPos, o.skip);
      if (collide) {
        const displaced = this.tryShiftCollideForResize(node, nextPos, collide);
        if (displaced) {
          // If multiple nodes are still blocking after the horizontal shift,
          // let GridStack resolve the remaining resize push naturally.
          if (this.collide(node, nextPos, o.skip)) {
            return super.moveNode(node, { ...o, x: requestedX, y: requestedY });
          }
        }
      }
      return super.moveNode(node, { ...o, x: requestedX, y: requestedY });
    }

    if (isActiveDrag) {
      const requestedX = typeof o.x === 'number' ? o.x : node.x;
      const requestedY = typeof o.y === 'number' ? o.y : node.y;
      const nextPos: GridStackNode = {
        ...node,
        x: requestedX,
        y: requestedY,
        w: requestedW,
        h: requestedH
      };
      this.nodeBoundFix(nextPos, false);

      const collide = this.collide(node, nextPos, o.skip);
      if (collide) {
        if (this.isNodeSwapThrottled(nodeId)) {
          return false;
        }

        // Require intentional penetration before swapping. Uses center crossing
        // by default, with a narrow left-edge fallback for large-vs-small cases
        // where center crossing is physically impossible at x=0.
        const deltaX = requestedX - (node.x ?? 0);
        const deltaY = requestedY - (node.y ?? 0);
        if (!this.hasPenetratedCenter(nextPos, collide, deltaX, deltaY)) {
          return false;
        }

        const collideId = this.getNodeId(collide);
        if (this.isSwapInCooldown(nodeId, collideId)) {
          return false;
        }

        const swapped = this.tryDirectPairSwap(node, collide, deltaX, deltaY);
        if (swapped) {
          movingNode._dirty = true;
          (collide as InternalGridNode)._dirty = true;
          this.setNodeSwapThrottle(nodeId, StableCompactGridStackEngine.SWAP_COOLDOWN_MS);
          this.setSwapCooldown(nodeId, collideId, StableCompactGridStackEngine.SWAP_COOLDOWN_MS);
          this.setSwapCooldown(collideId, nodeId, StableCompactGridStackEngine.SWAP_COOLDOWN_MS);
          (this as any)._notify?.();
          return true;
        }

        const multiSwapNodes = this.tryWideVerticalSplitSwap(node, nextPos, deltaX, deltaY);
        if (multiSwapNodes) {
          movingNode._dirty = true;
          this.setNodeSwapThrottle(nodeId, StableCompactGridStackEngine.SWAP_COOLDOWN_MS);
          multiSwapNodes.forEach(other => {
            (other as InternalGridNode)._dirty = true;
            const otherId = this.getNodeId(other);
            this.setSwapCooldown(nodeId, otherId, StableCompactGridStackEngine.SWAP_COOLDOWN_MS);
            this.setSwapCooldown(otherId, nodeId, StableCompactGridStackEngine.SWAP_COOLDOWN_MS);
          });
          (this as any)._notify?.();
          return true;
        }

        // Awkward sizes/constraints: reject the move and let the workspace-level
        // local compaction strategy run at drag stop.
        return false;
      }
    }

    return super.moveNode(node, o);
  }

  /**
   * Returns true only when the dragged node's center has crossed the collide node's
   * center in the direction of travel. This ensures a swap is intentional — the user
   * must drag more than halfway into the target before anything moves.
   */
  private hasPenetratedCenter(
    dragged: GridStackNode,
    collide: GridStackNode,
    dx: number,
    dy: number
  ): boolean {
    const dragLeft = dragged.x ?? 0;

    const dragCX = (dragged.x ?? 0) + (dragged.w ?? 1) / 2;
    const dragCY = (dragged.y ?? 0) + (dragged.h ?? 1) / 2;
    const colCX = (collide.x ?? 0) + (collide.w ?? 1) / 2;
    const colCY = (collide.y ?? 0) + (collide.h ?? 1) / 2;

    // For a primarily horizontal move, check horizontal penetration.
    // For a primarily vertical move, check vertical penetration.
    // When neither axis dominates, require both.
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (absX >= absY) {
      // Horizontal-dominant (or equal): must cross the collide center on X axis.
      if (dx > 0 && dragCX < colCX) return false;
      if (dx < 0 && dragCX > colCX) {
        // For leftward swaps, center-crossing can feel too strict with mixed
        // widths. Allow the swap once the dragged leading edge crosses colCX.
        const dragLeadingEdgeCrossed = dragLeft <= colCX;
        if (!dragLeadingEdgeCrossed) {
          return false;
        }
      }
    }

    if (absY >= absX) {
      // Vertical-dominant (or equal): must cross the collide center on Y axis.
      if (dy > 0 && dragCY < colCY) return false;
      if (dy < 0 && dragCY > colCY) return false;
    }

    return true;
  }

  private getNodeId(node: GridStackNode): number {
    const internalNode = node as GridStackNode & { _id?: number };
    const parsedId = Number.parseInt(String(node.id), 10) || 0;
    return internalNode._id ?? parsedId;
  }

  private isSwapInCooldown(nodeId: number, otherId: number): boolean {
    const cooldown = this._swapCooldownByNode.get(nodeId);
    if (!cooldown) {
      return false;
    }

    return cooldown.otherId === otherId && cooldown.expiresAt > Date.now();
  }

  private isNodeSwapThrottled(nodeId: number): boolean {
    const expiresAt = this._nodeSwapThrottle.get(nodeId);
    return typeof expiresAt === 'number' && expiresAt > Date.now();
  }

  private setNodeSwapThrottle(nodeId: number, durationMs = StableCompactGridStackEngine.SWAP_COOLDOWN_MS): void {
    this._nodeSwapThrottle.set(nodeId, Date.now() + durationMs);
  }

  private setSwapCooldown(nodeId: number, otherId: number, durationMs = StableCompactGridStackEngine.SWAP_COOLDOWN_MS): void {
    this._swapCooldownByNode.set(nodeId, {
      otherId,
      expiresAt: Date.now() + durationMs
    });
  }

  private tryDirectPairSwap(node: GridStackNode, collide: GridStackNode, dx: number, dy: number): boolean {
    const nodeX = node.x ?? 0;
    const nodeY = node.y ?? 0;
    const nodeW = node.w ?? 1;
    const nodeH = node.h ?? 1;
    const colX = collide.x ?? 0;
    const colY = collide.y ?? 0;
    const colW = collide.w ?? 1;
    const colH = collide.h ?? 1;

    const isVerticalSwap = Math.abs(dy) > Math.abs(dx);

    let nodeTarget: GridStackNode;
    let collideTarget: GridStackNode;

    if (isVerticalSwap) {
      // Height-aware adjacent placement for vertical reorder.
      let newNodeY: number;
      let newColY: number;

      if (colY >= nodeY) {
        // Node moving down.
        newColY = nodeY;
        newNodeY = nodeY + colH;
      } else {
        // Node moving up.
        newNodeY = colY;
        newColY = colY + nodeH;
      }

      const expectedNodeX = colX;
      const expectedColX = nodeX;
      const expectedNodeY = newNodeY;
      const expectedColY = newColY;

      nodeTarget = { x: colX, y: newNodeY, w: nodeW, h: nodeH };
      collideTarget = { x: nodeX, y: newColY, w: colW, h: colH };

      this.nodeBoundFix(nodeTarget, false);
      this.nodeBoundFix(collideTarget, false);

      if (
        nodeTarget.x !== expectedNodeX ||
        nodeTarget.y !== expectedNodeY ||
        collideTarget.x !== expectedColX ||
        collideTarget.y !== expectedColY
      ) {
        return false;
      }
    } else {
      // Width-aware adjacent placement for horizontal reorder.
      let newNodeX: number;
      let newColX: number;

      if (colX >= nodeX) {
        // Node moving right.
        newColX = nodeX;
        newNodeX = nodeX + colW;
      } else {
        // Node moving left.
        newNodeX = colX;
        newColX = colX + nodeW;
      }

      const expectedNodeX = newNodeX;
      const expectedColX = newColX;
      const expectedNodeY = colY;
      const expectedColY = nodeY;

      nodeTarget = { x: newNodeX, y: colY, w: nodeW, h: nodeH };
      collideTarget = { x: newColX, y: nodeY, w: colW, h: colH };

      this.nodeBoundFix(nodeTarget, false);
      this.nodeBoundFix(collideTarget, false);

      if (
        nodeTarget.x !== expectedNodeX ||
        nodeTarget.y !== expectedNodeY ||
        collideTarget.x !== expectedColX ||
        collideTarget.y !== expectedColY
      ) {
        return false;
      }
    }

    if (this.collide(node, nodeTarget, collide) || this.collide(collide, collideTarget, node)) {
      return false;
    }

    node.x = nodeTarget.x;
    node.y = nodeTarget.y;
    collide.x = collideTarget.x;
    collide.y = collideTarget.y;
    (node as InternalGridNode)._dirty = true;
    (collide as InternalGridNode)._dirty = true;
    return true;
  }

  /**
   * Handles one special multi-widget reorder: a wide lower widget dragged upward
   * above two narrower widgets that sit side-by-side on the same row.
   */
  private tryWideVerticalSplitSwap(
    node: GridStackNode,
    nextPos: GridStackNode,
    dx: number,
    dy: number
  ): GridStackNode[] | null {
    if (!(Math.abs(dy) > Math.abs(dx) && dy < 0)) {
      return null;
    }

    const overlaps = this.nodes
      .filter(other => other !== node && this.rectsOverlap(nextPos, other))
      .sort((a, b) => (a.x ?? 0) - (b.x ?? 0));

    if (overlaps.length !== 2) {
      return null;
    }

    const left = overlaps[0];
    const right = overlaps[1];
    const leftX = left.x ?? 0;
    const rightX = right.x ?? 0;
    const leftY = left.y ?? 0;
    const rightY = right.y ?? 0;
    const leftW = left.w ?? 1;
    const rightW = right.w ?? 1;
    const leftH = left.h ?? 1;
    const rightH = right.h ?? 1;
    const nodeX = node.x ?? 0;
    const nodeY = node.y ?? 0;
    const nodeW = node.w ?? 1;
    const nodeH = node.h ?? 1;

    // Two target widgets must be on the same top row and non-overlapping.
    if (leftY !== rightY || leftH !== rightH || rightX < leftX + leftW) {
      return null;
    }

    const topBandX = leftX;
    const topBandRight = rightX + rightW;
    if (topBandRight - topBandX > nodeW || leftY >= nodeY) {
      return null;
    }

    // Wide node should cover the two widgets' horizontal span when moving up.
    if (nodeX > topBandX || nodeX + nodeW < topBandRight) {
      return null;
    }

    const nodeTarget: GridStackNode = { x: topBandX, y: leftY, w: nodeW, h: nodeH };
    const leftTarget: GridStackNode = { x: leftX, y: nodeY, w: leftW, h: leftH };
    const rightTarget: GridStackNode = { x: rightX, y: nodeY, w: rightW, h: rightH };

    this.nodeBoundFix(nodeTarget, false);
    this.nodeBoundFix(leftTarget, false);
    this.nodeBoundFix(rightTarget, false);

    if (
      nodeTarget.x !== topBandX ||
      nodeTarget.y !== leftY ||
      leftTarget.x !== leftX ||
      leftTarget.y !== nodeY ||
      rightTarget.x !== rightX ||
      rightTarget.y !== nodeY
    ) {
      return null;
    }

    const ignoreNodes = [node, left, right];
    if (
      this.collidesWithAny(nodeTarget, ignoreNodes) ||
      this.collidesWithAny(leftTarget, ignoreNodes) ||
      this.collidesWithAny(rightTarget, ignoreNodes)
    ) {
      return null;
    }

    // Ensure the moved split nodes don't overlap each other at target row.
    if (this.rectsOverlap(leftTarget, rightTarget)) {
      return null;
    }

    node.x = nodeTarget.x;
    node.y = nodeTarget.y;
    left.x = leftTarget.x;
    left.y = leftTarget.y;
    right.x = rightTarget.x;
    right.y = rightTarget.y;
    return [left, right];
  }

  private collidesWithAny(target: GridStackNode, ignore: GridStackNode[]): boolean {
    return this.nodes.some(other => {
      if (ignore.includes(other)) {
        return false;
      }
      return this.rectsOverlap(target, other);
    });
  }

  private rectsOverlap(a: GridStackNode, b: GridStackNode): boolean {
    const ax = a.x ?? 0;
    const ay = a.y ?? 0;
    const aw = a.w ?? 1;
    const ah = a.h ?? 1;
    const bx = b.x ?? 0;
    const by = b.y ?? 0;
    const bw = b.w ?? 1;
    const bh = b.h ?? 1;

    return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
  }

  /**
   * For resize-only collisions, attempt to keep layout stable by shifting the
   * collided neighbour horizontally on the same row instead of pushing it down.
   */
  private tryShiftCollideForResize(
    node: GridStackNode,
    nextPos: GridStackNode,
    collide: GridStackNode
  ): boolean {
    const nodeX = node.x ?? 0;
    const nextX = nextPos.x ?? nodeX;
    const nextW = nextPos.w ?? (node.w ?? 1);
    const nodeW = node.w ?? 1;

    const isGrowing = nextW > nodeW;
    if (!isGrowing) {
      return false;
    }

    // Determine which side is expanding.
    const growsLeft = nextX < nodeX;

    // If widening to the right, allow a same-row push chain so the left-most
    // widget can expand and push neighbours right without any vertical cascade.
    if (!growsLeft) {
      const shift = nextW - nodeW;
      return this.tryShiftResizeRowChainRight(node, collide, shift);
    }

    const targetX = growsLeft
      ? nextX - (collide.w ?? 1)
      : nextX + nextW;

    const targetRect: GridStackNode = {
      x: targetX,
      y: collide.y ?? 0,
      w: collide.w ?? 1,
      h: collide.h ?? 1
    };

    // Keep the neighbour on-grid; if it must be clamped, the move isn't valid.
    const bounded = { ...targetRect } as GridStackNode;
    this.nodeBoundFix(bounded, false);
    if (
      bounded.x !== targetRect.x ||
      bounded.y !== targetRect.y ||
      bounded.w !== targetRect.w ||
      bounded.h !== targetRect.h
    ) {
      return false;
    }

    // Ignore the resizing node while testing the neighbour's new position.
    if (this.collide(collide, bounded, node)) {
      return false;
    }

    collide.x = bounded.x;
    collide.y = bounded.y;
    (collide as InternalGridNode)._dirty = true;
    return true;
  }

  /**
   * Shift a same-row chain of neighbours to the right by `shift` columns.
   * Returns false when any node would go out-of-bounds or collide with other rows.
   */
  private tryShiftResizeRowChainRight(
    node: GridStackNode,
    firstCollide: GridStackNode,
    shift: number
  ): boolean {
    if (shift <= 0) {
      return true;
    }

    const rowY = firstCollide.y ?? 0;
    const rowH = firstCollide.h ?? 1;
    const rowBottom = rowY + rowH;

    // Consider widgets that intersect the collide row band and sit to the right
    // of the resized node's current anchor.
    const candidates = this.nodes
      .filter(other => other !== node)
      .filter(other => {
        const otherX = other.x ?? 0;
        const otherY = other.y ?? 0;
        const otherH = other.h ?? 1;
        const otherBottom = otherY + otherH;
        const intersectsRow = otherY < rowBottom && otherBottom > rowY;
        return intersectsRow && otherX >= (node.x ?? 0);
      })
      .sort((a, b) => (a.x ?? 0) - (b.x ?? 0));

    // Build a contiguous overlap chain starting from first collided node.
    const chain: GridStackNode[] = [];
    const firstIndex = candidates.indexOf(firstCollide);
    if (firstIndex < 0) {
      return false;
    }
    chain.push(firstCollide);

    let rightEdge = (firstCollide.x ?? 0) + (firstCollide.w ?? 1);
    for (let i = firstIndex + 1; i < candidates.length; i += 1) {
      const other = candidates[i];
      const otherX = other.x ?? 0;
      const otherW = other.w ?? 1;
      if (otherX < rightEdge + shift) {
        chain.push(other);
        rightEdge = Math.max(rightEdge, otherX + otherW);
      }
    }

    // Validate all shifted targets first (right-most to left-most).
    const planned = new Map<GridStackNode, GridStackNode>();
    for (let i = chain.length - 1; i >= 0; i -= 1) {
      const current = chain[i];
      const target: GridStackNode = {
        x: (current.x ?? 0) + shift,
        y: current.y ?? 0,
        w: current.w ?? 1,
        h: current.h ?? 1
      };

      const bounded = { ...target } as GridStackNode;
      this.nodeBoundFix(bounded, false);
      if (
        bounded.x !== target.x ||
        bounded.y !== target.y ||
        bounded.w !== target.w ||
        bounded.h !== target.h
      ) {
        return false;
      }

      planned.set(current, bounded);
    }

    // Ensure planned targets don't collide with non-chain widgets.
    for (let i = 0; i < chain.length; i += 1) {
      const current = chain[i];
      const target = planned.get(current)!;
      const collides = this.nodes.some(other => {
        if (other === node || chain.includes(other)) {
          return false;
        }
        return this.rectsOverlap(target, other);
      });
      if (collides) {
        return false;
      }
    }

    // Apply moves right-to-left so intermediate states cannot overlap.
    for (let i = chain.length - 1; i >= 0; i -= 1) {
      const current = chain[i];
      const target = planned.get(current)!;
      current.x = target.x;
      current.y = target.y;
      (current as InternalGridNode)._dirty = true;
    }

    return true;
  }
}
